import { Loader, ScrollText, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useContract, useViewTiedContracts } from "@/api/contract-query";
import TransactionTable from "@/components/data-table/transaction-table";
import OrderReceipt from "@/components/order/order-receipt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import useDebounce from "@/hooks/use-debounce";
import { cn } from "@/lib/utils";
import useAuthStore from "@/store/auth";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { BankSection } from "./bank-section";
import { WalletCard } from "./wallet-card";

export default function HomeComponent() {
	const user = useAuthStore(state => state.user);

	return (
		<div className="container p-4 mx-auto">
			{user?.userType === "BUYER" && <SearchInput />}
			<div className="grid gap-6 lg:grid-cols-[400px,1fr]">
				<div className="space-y-6">
					<WalletCard />
					<BankSection />
				</div>
				<TransactionTable />
			</div>
		</div>
	);
}

function SearchInput({ className }: { className?: string }) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const searchValue = useDebounce(search);

	const searchParams = useSearch({ from: "/buyer/home" });

	const { data: tiedContracts, isPending: tiedContractsPending } = useViewTiedContracts();
	const navigate = useNavigate();

	const sortedContracts = useMemo(() => {
		return tiedContracts?.items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()).slice(0, 5);
	}, [tiedContracts]);

	const handleClick = (contractCode: string) => {
		navigate({ to: "/buyer/home", search: { contractCode } });
	};

	const { data: contract, isError, isPending, error } = useContract({ contractCode: searchValue });

	useEffect(() => {
		if (searchParams.contractCode) {
			setSearch(searchParams.contractCode.toString());
		}
	}, [searchParams.contractCode]);

	return (
		<>

			<div className={cn("relative mb-4", className)}>
				<Input
					className="w-full bg-gray-50 px-4 py-6 pl-4 pr-12 placeholder:text-muted-foreground placeholder:text-sm"

					placeholder="Search Contract"
					onChange={e => setSearch(e.target.value)}
					value={search}
				/>
				{search.length === 0
					? <Search className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500" />
					:	(
							<X
								role="button"
								onClick={() => setSearch("")}
								className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-500"
							/>
						)}
				{search && (
					<div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-10">
						{isPending
							? (
									<div className="flex items-center justify-center p-2">
										<Loader className="animate-spin text-muted-foreground" />
									</div>
								)
							: isError
								? (
										<div className="p-2">
											{error?.message.includes("status code 404") ? "Contract not found" : "Error loading results"}
										</div>
									)
								: contract
									? (
											<div className="flex justify-between items-center p-2">
												<span className="flex items-center gap-2">
													<ScrollText className="size-10 text-hover-secondary bg-primary p-2 rounded-md" />
													{contract.title}
												</span>
												<OrderReceipt contract={contract} open={open} onOpenChange={setOpen} />
											</div>
										)
									: (
											<div className="p-2">No results found</div>
										)}
					</div>
				)}

			</div>

			<p className="text-sm text-muted-foreground block">Recent Contracts</p>
			<div className="my-2 flex flex-wrap gap-2">
				{

					tiedContractsPending
						? Array.from({ length: 5 }).map(() => (
								<Skeleton className="w-20 h-6 animate-pulse" key={crypto.randomUUID()} />
							))
						: sortedContracts?.map(contract => (
							<Button variant="outline" onClick={() => handleClick(contract.contractCode)} className="h-6 bg-hover-secondary hover:bg-hover-secondary border-primary/50 border" key={contract.id}>
								{contract.title}
							</Button>
						))
				}
			</div>
		</>
	);
}
